import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PropTypes from "prop-types";
import { API_ENDPOINT } from "../../../constants/constants";
import { getDbHeader } from "../../../utils2024/utils";

const CategorySelectorAccordions = ({
  title = "",
  onSelectionChange,
  selectedCategories: propSelectedCategories = null,
  isOpen = false,
}) => {
  const [productCategories, setProductCategories] = useState([]);
  const [categoryType, setCategoryType] = useState(null); // "single" | "double" | "triple"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Controlled vs uncontrolled mode
  const [internalSelectedCategories, setInternalSelectedCategories] = useState(
    []
  );
  const isControlled = propSelectedCategories !== null;
  const selectedCategories = isControlled
    ? propSelectedCategories
    : internalSelectedCategories;

  // ✅ Fetch categories when dialog opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_ENDPOINT}/event/get-product-categories`,
          {
            method: "GET",
            ...getDbHeader(),
          }
        );

        if (!response.ok) throw new Error("Failed to load categories");
        const data = await response.json();

        // API gives { type, categories: [...] }
        setCategoryType(data?.type || null);
        setProductCategories(data?.categories || []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [isOpen]);

  // ✅ Shared selection updater (used by both single and double/triple)
  const applySelection = useCallback(
    (newSelections) => {
      if (!isControlled) {
        setInternalSelectedCategories(newSelections);
      }
      onSelectionChange?.(newSelections);
    },
    [isControlled, onSelectionChange]
  );

  // ─── SINGLE type ─────────────────────────────────────────────────────────────

  // Options for the Autocomplete — one entry per unique MainCategory
  const singleOptions = useMemo(() => {
    if (categoryType !== "single" || !productCategories.length) return [];
    const seen = new Set();
    return productCategories.reduce((acc, cat) => {
      const main = cat.MainCategory?.trim();
      if (main && !seen.has(main)) {
        seen.add(main);
        acc.push({ MainCategory: main, Category: null, SubCategory: null });
      }
      return acc;
    }, []);
  }, [categoryType, productCategories]);

  // Current Autocomplete value — only single-type selections
  const singleValue = useMemo(
    () =>
      selectedCategories.filter(
        (sel) => sel.Category === null && sel.SubCategory === null
      ),
    [selectedCategories]
  );

  const handleSingleChange = useCallback(
    (_event, newValue) => {
      applySelection(newValue);
    },
    [applySelection]
  );

  // ─── DOUBLE / TRIPLE types ────────────────────────────────────────────────────

  // ✅ Group data into Main → Category → Subcategory (for double/triple)
  const groupedData = useMemo(() => {
    if (categoryType === "single" || !productCategories.length) return {};

    return productCategories.reduce((acc, curr) => {
      const mainCategory = curr.MainCategory?.trim();
      const category = curr.Category?.trim();
      const subCategory = curr.SubCategory?.trim();

      if (!mainCategory) return acc;

      if (!acc[mainCategory]) {
        acc[mainCategory] = { name: mainCategory, categories: {} };
      }

      if (category) {
        if (!acc[mainCategory].categories[category]) {
          acc[mainCategory].categories[category] = {
            name: category,
            subcategories: [],
          };
        }

        if (subCategory) {
          acc[mainCategory].categories[category].subcategories.push({
            name: subCategory,
          });
        }
      }

      return acc;
    }, {});
  }, [categoryType, productCategories]);

  // ✅ Check selection (double/triple)
  const isSubcategorySelected = useCallback(
    (sub, mainName, categoryName) =>
      selectedCategories.some(
        (sel) =>
          sel.mainCat === mainName &&
          sel.subCat === categoryName &&
          sel.subSubCat === sub.name
      ),
    [selectedCategories]
  );

  // ✅ Toggle selection (double/triple)
  const handleCheckboxChange = useCallback(
    (sub, mainName, categoryName) => {
      const catObj = {
        mainCat: mainName,
        subCat: categoryName,
        subSubCat: sub.name,
      };

      const prev = isControlled
        ? propSelectedCategories
        : internalSelectedCategories;

      const exists = prev.some(
        (sel) =>
          sel.mainCat === catObj.mainCat &&
          sel.subCat === catObj.subCat &&
          sel.subSubCat === catObj.subSubCat
      );

      applySelection(
        exists
          ? prev.filter(
              (sel) =>
                !(
                  sel.mainCat === catObj.mainCat &&
                  sel.subCat === catObj.subCat &&
                  sel.subSubCat === catObj.subSubCat
                )
            )
          : [...prev, catObj]
      );
    },
    [
      isControlled,
      propSelectedCategories,
      internalSelectedCategories,
      applySelection,
    ]
  );

  // ─── UI states ────────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  if (loading)
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
        <Typography variant="body2" mt={1}>
          Loading categories...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Alert severity="error">
        {error.message || "Failed to load categories"}
      </Alert>
    );

  if (!productCategories.length)
    return <Alert severity="info">No categories available</Alert>;

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box my={2}>
      {title && (
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
      )}

      {categoryType === "single" ? (
        /* ── Multi-select Autocomplete for SINGLE ── */
        <Autocomplete
          multiple
          limitTags={5}
          disableCloseOnSelect
          options={singleOptions}
          value={singleValue}
          getOptionLabel={(option) => option.MainCategory}
          isOptionEqualToValue={(option, value) =>
            option.MainCategory === value.MainCategory
          }
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} style={{ marginRight: 8 }} />
              {option.MainCategory}
            </li>
          )}
          onChange={handleSingleChange}
          renderInput={(params) => (
            <TextField {...params} label="Select Main Categories" />
          )}
        />
      ) : (
        /* ── Accordion layout for DOUBLE / TRIPLE ── */
        Object.values(groupedData).map((mainCategory) => (
          <Accordion key={mainCategory.name}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold">
                {mainCategory.name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.values(mainCategory.categories).map((category) => (
                <Accordion key={category.name} sx={{ ml: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {category.name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {category.subcategories.map((sub) => (
                      <FormControlLabel
                        key={sub.name}
                        sx={{ width: "100%" }}
                        control={
                          <Checkbox
                            size="small"
                            checked={isSubcategorySelected(
                              sub,
                              mainCategory.name,
                              category.name
                            )}
                            onChange={() =>
                              handleCheckboxChange(
                                sub,
                                mainCategory.name,
                                category.name
                              )
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">{sub.name}</Typography>
                        }
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

CategorySelectorAccordions.propTypes = {
  title: PropTypes.string,
  onSelectionChange: PropTypes.func,
  selectedCategories: PropTypes.array,
  isOpen: PropTypes.bool,
};

export default React.memo(CategorySelectorAccordions);
